import { ValueOf } from './shared.interface';

export interface DocumentTypeInterface {
  id: number;
  aadeId: string;
  aadeCode: DOC_TYPE_AADE_CODE_VALUES;
  name: string;
  description: string;
  createdAt: Date;
  updatedAt: Date;
}

export const DOC_TYPE_AADE_CODE = {
  PRO_FORMA_INVOICE: '0.0',
  INVOICE: '1.1', // Viva supported
  INVOICE_INTRA_COMMUNITY_DELIVERIES: '1.2', // Viva supported
  INVOICE_THIRD_COUNTRIES_DELIVERIES: '1.3', // Viva supported
  INVOICE_SALES_FOR_THIRD_PARTIES: '1.4',
  INVOICE_CLEARANCE_FOR_THIRD_PARTIES: '1.5',
  INVOICE_SUPPLEMENTARY_DOCUMENT: '1.6',
  INVOICE_SERVICES: '2.1', // Viva supported
  INVOICE_SERVICES_INTRA_COMMUNITY: '2.2', // Viva supported
  INVOICE_SERVICES_THIRD_COUNTRIES: '2.3', // Viva supported
  INVOICE_SERVICES_SUPPLEMENTARY_DOCUMENT: '2.4',
  // Cannot be marked as a delivery note - myDATA rejects every delivery-related field outright
  // (DispatchDate/DispatchTime/MovePurpose/VehicleNumber/IsDeliveryNote/otherDeliveryNoteHeader
  // all come back "forbidden for this invoice type", confirmed via a real Viva rejection). Not in
  // `DOC_TYPE_ALLOWED_AS_DELIVERY_NOTE` below for this reason.
  TITLE_DEED_NON_OBLIGATORY_ISSUER: '3.1',
  TITLE_DEED_DENIAL_OF_VALID_ISSUER: '3.2',
  INVOICE_RETURN_RELATED: '5.1', // Viva supported - Cannot be enabled since we don't have `correlatedInvoice` field in FE
  INVOICE_RETURN_UNRELATED: '5.2', // Viva supported
  /**
   * @deprecated - Not supported in MyData
   * Marked with `entity.subscription: 999` in order to be skipped always
   */
  INVOICE_RETURN_CREDIT_UNRELATED: '5.3',
  SELF_DELIVERY_ITEM: '6.1',
  SELF_USE_ITEM: '6.2',
  CONTRACT_INCOME: '7.1',
  RENT_INCOME: '8.1',
  CLIMATE_CRISIS_RESILIENCE_TAX: '8.2',
  RECEIPT_MONEY_COLLECTION_POS: '8.4', // Viva supported
  RECEIPT_MONEY_RETURN_POS: '8.5', // Viva supported
  CATERING_DELIVERY_NOTE: '8.6', // Viva supported
  /**
   * @deprecated - Not supported in MyData
   * Not in database
   */
  ESHOP_DELIVERY_NOTE: '8.7',
  /**
   * Δελτίο Αποστολής Συσχετιζόμενο
   * NOT yet added in our envs - NOT supported by Viva
   */
  DELIVERY_NOTE_RELATED: '9.1',
  /**
   * Συγκεντρωτικό Δελτίο Αποστολής
   * NOT yet added in our envs - NOT supported by Viva
   */
  DELIVERY_NOTE_GROUP: '9.2',
  DELIVERY_NOTE: '9.3',
  INVERTED_DELIVERY_NOTE: '9.33',
  /**
   * Δελτίο Ποσοτικής Παραλαβής Συσχετιζόμενο
   * NOT supported by Viva
   */
  QUANTITY_RECEIPT_FORM_RELATED: '10.1',
  /**
   * Δελτίο Ποσοτικής Παραλαβής Μη Συσχετιζόμενο
   * NOT supported by Viva
   */
  QUANTITY_RECEIPT_FORM: '10.2',
  RECEIPT: '11.1', // Viva supported
  RECEIPT_SERVICES: '11.2', // Viva supported
  /**
   * @deprecated - Not supported from Viva Fiscal
   * Not in database
   */
  SIMPLIFIED_INVOICE: '11.3',
  RECEIPT_RETURN_RELATED: '11.4', // Viva supported
  RECEIPT_SALES_FOR_THIRD_PARTIES: '11.5',
} as const;

export type DOC_TYPE_AADE_CODE_VALUES = ValueOf<typeof DOC_TYPE_AADE_CODE>;

/**
 * This should be updated whenever the provider starts supporting a new doc type
 * @deprecated
 * Currently not used since if we enable `post_with_provider` for Invoices
 * all invoice doc types should be posted with provider.
 * So we are allowing to select all invoice types when creating a doc with provider
 * and if the doc type is not supported an error will be shown on posting.
 * Same for Receipt doc types
 */
export const SUPPORTED_PROVIDER_DOCS: Partial<DOC_TYPE_AADE_CODE_VALUES>[] = [
  DOC_TYPE_AADE_CODE.INVOICE,
  DOC_TYPE_AADE_CODE.INVOICE_INTRA_COMMUNITY_DELIVERIES,
  DOC_TYPE_AADE_CODE.INVOICE_THIRD_COUNTRIES_DELIVERIES,
  DOC_TYPE_AADE_CODE.INVOICE_SERVICES,
  DOC_TYPE_AADE_CODE.INVOICE_SERVICES_INTRA_COMMUNITY,
  DOC_TYPE_AADE_CODE.INVOICE_SERVICES_THIRD_COUNTRIES,
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_RELATED,
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_UNRELATED,
  DOC_TYPE_AADE_CODE.RECEIPT_MONEY_COLLECTION_POS,
  DOC_TYPE_AADE_CODE.RECEIPT_MONEY_RETURN_POS,
  DOC_TYPE_AADE_CODE.CATERING_DELIVERY_NOTE,
  DOC_TYPE_AADE_CODE.RECEIPT,
  DOC_TYPE_AADE_CODE.RECEIPT_SERVICES,
  DOC_TYPE_AADE_CODE.RECEIPT_RETURN_RELATED,
];

export const INVOICE_TO_PERSON_AADE_CODES: Partial<DOC_TYPE_AADE_CODE_VALUES>[] = [
  DOC_TYPE_AADE_CODE.TITLE_DEED_NON_OBLIGATORY_ISSUER,
  DOC_TYPE_AADE_CODE.TITLE_DEED_DENIAL_OF_VALID_ISSUER,
];

export const INVOICE_AADE_CODES: Partial<DOC_TYPE_AADE_CODE_VALUES>[] = [
  DOC_TYPE_AADE_CODE.INVOICE,
  DOC_TYPE_AADE_CODE.INVOICE_INTRA_COMMUNITY_DELIVERIES,
  DOC_TYPE_AADE_CODE.INVOICE_THIRD_COUNTRIES_DELIVERIES,
  DOC_TYPE_AADE_CODE.INVOICE_SALES_FOR_THIRD_PARTIES,
  DOC_TYPE_AADE_CODE.INVOICE_CLEARANCE_FOR_THIRD_PARTIES,
  DOC_TYPE_AADE_CODE.INVOICE_SUPPLEMENTARY_DOCUMENT,
  DOC_TYPE_AADE_CODE.INVOICE_SERVICES,
  DOC_TYPE_AADE_CODE.INVOICE_SERVICES_INTRA_COMMUNITY,
  DOC_TYPE_AADE_CODE.INVOICE_SERVICES_THIRD_COUNTRIES,
  DOC_TYPE_AADE_CODE.INVOICE_SERVICES_SUPPLEMENTARY_DOCUMENT,
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_RELATED,
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_UNRELATED,
  DOC_TYPE_AADE_CODE.SIMPLIFIED_INVOICE,
  ...INVOICE_TO_PERSON_AADE_CODES,
];

export const RECEIPT_AADE_CODES: Partial<DOC_TYPE_AADE_CODE_VALUES>[] = [
  DOC_TYPE_AADE_CODE.RECEIPT,
  DOC_TYPE_AADE_CODE.RECEIPT_SERVICES,
  DOC_TYPE_AADE_CODE.RECEIPT_RETURN_RELATED,
  DOC_TYPE_AADE_CODE.RECEIPT_SALES_FOR_THIRD_PARTIES,
];

export const LIST_OF_AADE_IDS_OF_RETURN_DOC_TYPES: Partial<DOC_TYPE_AADE_CODE_VALUES>[] = [
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_RELATED,
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_UNRELATED,
  DOC_TYPE_AADE_CODE.RECEIPT_RETURN_RELATED,
];

export const UNPOSTABLE_AADE_DOC_TYPE_CODES: Partial<DOC_TYPE_AADE_CODE_VALUES>[] = [
  DOC_TYPE_AADE_CODE.PRO_FORMA_INVOICE,
];

export const SUPPLIER_DOC_TYPE = [DOC_TYPE_AADE_CODE.TITLE_DEED_NON_OBLIGATORY_ISSUER];

export const DOC_TYPE_ALLOWED_AS_DELIVERY_NOTE: Partial<DOC_TYPE_AADE_CODE_VALUES>[] = [
  DOC_TYPE_AADE_CODE.INVOICE,
  DOC_TYPE_AADE_CODE.INVOICE_INTRA_COMMUNITY_DELIVERIES,
  DOC_TYPE_AADE_CODE.INVOICE_THIRD_COUNTRIES_DELIVERIES,
  DOC_TYPE_AADE_CODE.RECEIPT,
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_RELATED,
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_UNRELATED,
  DOC_TYPE_AADE_CODE.RECEIPT_RETURN_RELATED,
  DOC_TYPE_AADE_CODE.INVOICE_SALES_FOR_THIRD_PARTIES,
  DOC_TYPE_AADE_CODE.RECEIPT_SALES_FOR_THIRD_PARTIES,
];

// Doc types where the `isDeliveryNote` toggle is shown (see `DOC_TYPE_ALLOWED_AS_DELIVERY_NOTE`
// above) but should NOT get `DeliveryNoteToggle`'s usual auto-enable-on-switch/clone/fresh-form
// behavior - it should render off and stay off until the user explicitly turns it on.
export const DOC_TYPE_DELIVERY_NOTE_DISABLED_BY_DEFAULT: Partial<DOC_TYPE_AADE_CODE_VALUES>[] = [
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_RELATED,
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_UNRELATED,
];
